import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { customerService } from '../services';

export interface Address {
  id: string;
  label: string;  // Changed from 'type' to match backend
  street: string; // Changed from 'address' to match backend
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  isDefault: boolean;
  // Optional fields for additional information
  name?: string;
  phone?: string;
}

interface AddressState {
  addresses: Address[];
  selectedAddressId: string | null;
  isLoading: boolean;
}

type AddressAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ADDRESSES'; payload: Address[] }
  | { type: 'ADD_ADDRESS'; payload: Address }
  | { type: 'UPDATE_ADDRESS'; payload: Address }
  | { type: 'DELETE_ADDRESS'; payload: string }
  | { type: 'SET_DEFAULT_ADDRESS'; payload: string }
  | { type: 'SELECT_ADDRESS'; payload: string | null }
  | { type: 'CLEAR_ADDRESSES' };

const initialState: AddressState = {
  addresses: [],
  selectedAddressId: null,
  isLoading: false,
};

const addressReducer = (state: AddressState, action: AddressAction): AddressState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ADDRESSES':
      return { 
        ...state, 
        addresses: action.payload,
        selectedAddressId: action.payload.find(addr => addr.isDefault)?.id || null
      };
    
    case 'ADD_ADDRESS': {
      const newAddresses = [...state.addresses];
      // If this is the first address or marked as default, make it default
      if (action.payload.isDefault || newAddresses.length === 0) {
        // Remove default from other addresses
        newAddresses.forEach(addr => addr.isDefault = false);
        action.payload.isDefault = true;
      }
      newAddresses.push(action.payload);
      return { 
        ...state, 
        addresses: newAddresses,
        selectedAddressId: action.payload.isDefault ? action.payload.id : state.selectedAddressId
      };
    }
    
    case 'UPDATE_ADDRESS': {
      const updatedAddresses = state.addresses.map(addr => {
        if (addr.id === action.payload.id) {
          return action.payload;
        }
        // If the updated address is marked as default, remove default from others
        if (action.payload.isDefault && addr.id !== action.payload.id) {
          return { ...addr, isDefault: false };
        }
        return addr;
      });
      return { 
        ...state, 
        addresses: updatedAddresses,
        selectedAddressId: action.payload.isDefault ? action.payload.id : state.selectedAddressId
      };
    }
    
    case 'DELETE_ADDRESS': {
      const filteredAddresses = state.addresses.filter(addr => addr.id !== action.payload);
      // If deleted address was default, make first address default
      if (filteredAddresses.length > 0 && !filteredAddresses.some(addr => addr.isDefault)) {
        filteredAddresses[0].isDefault = true;
      }
      return { 
        ...state, 
        addresses: filteredAddresses,
        selectedAddressId: state.selectedAddressId === action.payload 
          ? (filteredAddresses[0]?.id || null) 
          : state.selectedAddressId
      };
    }
    
    case 'SET_DEFAULT_ADDRESS': {
      const addressesWithNewDefault = state.addresses.map(addr => ({
        ...addr,
        isDefault: addr.id === action.payload
      }));
      return { 
        ...state, 
        addresses: addressesWithNewDefault,
        selectedAddressId: action.payload
      };
    }
    
    case 'SELECT_ADDRESS':
      return { ...state, selectedAddressId: action.payload };
    
    case 'CLEAR_ADDRESSES':
      return initialState;
    
    default:
      return state;
  }
};

interface AddressContextType extends AddressState {
  addAddress: (address: Omit<Address, 'id'>) => Promise<void>;
  updateAddress: (address: Address) => Promise<void>;
  deleteAddress: (id: string) => Promise<void>;
  setDefaultAddress: (id: string) => Promise<void>;
  selectAddress: (id: string | null) => void;
  getSelectedAddress: () => Address | null;
  loadAddresses: () => Promise<void>;
}

const AddressContext = createContext<AddressContextType | undefined>(undefined);

export const useAddress = (): AddressContextType => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within an AddressProvider');
  }
  return context;
};

interface AddressProviderProps {
  children: React.ReactNode;
}

export const AddressProvider: React.FC<AddressProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(addressReducer, initialState);
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  
  // Use refs to prevent infinite loops
  const hasLoadedAddresses = useRef(false);
  const currentUserId = useRef<string | null>(null);

  const loadAddresses = useCallback(async (): Promise<void> => {
    if (!user) return;

    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const response = await customerService.getAddresses(user.id);
      if (response.success) {
        dispatch({ type: 'SET_ADDRESSES', payload: response.data });
      } else {
        dispatch({ type: 'SET_ADDRESSES', payload: [] });
        if (response.error && !response.error.includes('404')) {
          showToast(response.error || 'Failed to load addresses', 'error');
        }
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      dispatch({ type: 'SET_ADDRESSES', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [user, showToast]);

  // Load addresses when user changes
  useEffect(() => {
    // Don't do anything while auth is still loading
    if (isLoading) return;
    
    if (isAuthenticated && user) {
      // Only load addresses if user ID has changed or addresses haven't been loaded yet
      if (currentUserId.current !== user.id || !hasLoadedAddresses.current) {
        currentUserId.current = user.id;
        hasLoadedAddresses.current = true;
        loadAddresses();
      }
    } else {
      // Reset flags when user logs out
      hasLoadedAddresses.current = false;
      currentUserId.current = null;
      dispatch({ type: 'CLEAR_ADDRESSES' });
    }
  }, [isAuthenticated, user, isLoading, loadAddresses]);


  const addAddress = async (addressData: Omit<Address, 'id'>): Promise<void> => {
    if (!user) return;

    try {
      const response = await customerService.addAddress(user.id, addressData);
      if (response.success && response.data) {
        dispatch({ type: 'ADD_ADDRESS', payload: response.data });
        showToast('Address added successfully', 'success');
      } else {
        showToast(response.error || 'Failed to add address', 'error');
      }
    } catch (error) {
      console.error('Error adding address:', error);
      showToast('Failed to add address', 'error');
    }
  };

  const updateAddress = async (address: Address): Promise<void> => {
    if (!user) return;

    try {
      const response = await customerService.updateAddress(user.id, address.id, address);
      if (response.success && response.data) {
        dispatch({ type: 'UPDATE_ADDRESS', payload: response.data });
        showToast('Address updated successfully', 'success');
      } else {
        showToast(response.error || 'Failed to update address', 'error');
      }
    } catch (error) {
      console.error('Error updating address:', error);
      showToast('Failed to update address', 'error');
    }
  };

  const deleteAddress = async (id: string): Promise<void> => {
    if (!user) return;

    try {
      const response = await customerService.deleteAddress(user.id, id);
      if (response.success) {
        dispatch({ type: 'DELETE_ADDRESS', payload: id });
        showToast('Address deleted successfully', 'success');
      } else {
        showToast(response.error || 'Failed to delete address', 'error');
      }
    } catch (error) {
      console.error('Error deleting address:', error);
      showToast('Failed to delete address', 'error');
    }
  };

  const setDefaultAddress = async (id: string): Promise<void> => {
    if (!user) return;

    try {
      const response = await customerService.setDefaultAddress(user.id, id);
      if (response.success) {
        dispatch({ type: 'SET_DEFAULT_ADDRESS', payload: id });
        showToast('Default address updated', 'success');
      } else {
        showToast(response.error || 'Failed to set default address', 'error');
      }
    } catch (error) {
      console.error('Error setting default address:', error);
      showToast('Failed to set default address', 'error');
    }
  };

  const selectAddress = (id: string | null): void => {
    dispatch({ type: 'SELECT_ADDRESS', payload: id });
  };

  const getSelectedAddress = (): Address | null => {
    return state.addresses.find(addr => addr.id === state.selectedAddressId) || null;
  };

  const value: AddressContextType = {
    ...state,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
    selectAddress,
    getSelectedAddress,
    loadAddresses,
  };

  return (
    <AddressContext.Provider value={value}>
      {children}
    </AddressContext.Provider>
  );
};